/**
 * ComplianceReportsPage - Admin Dashboard for Shift Compliance Reports
 * 
 * Features:
 * - List completed shifts
 * - Filter by officer, site, date range
 * - Generate and download compliance PDF reports
 * - View report summary statistics
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Search,
  Filter,
  Calendar,
  User,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Shield,
  FileWarning,
  Eye,
} from 'lucide-react';

export default function ComplianceReportsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    officer_id: '',
    site_id: '',
    start_date: '',
    end_date: '',
  });
  
  // Lists for filters
  const [officers, setOfficers] = useState([]);
  const [sites, setSites] = useState([]);
  
  // Generation state
  const [generating, setGenerating] = useState(null);
  
  // Fetch data on mount
  useEffect(() => {
    fetchData();
    fetchFilters();
    fetchSummary();
  }, []);
  
  // Fetch completed shifts
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.officer_id) params.append('officer_id', filters.officer_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', '50');
      
      const response = await api.get(`/reports/compliance/shifts?${params.toString()}`);
      setShifts(response.data.shifts || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch filter options
  const fetchFilters = async () => {
    try {
      const [usersRes, sitesRes] = await Promise.all([
        api.get('/users?roles=security_officer,supervisor&is_active=true'),
        api.get('/sites'),
      ]);
      setOfficers(usersRes.data || []);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };
  
  // Fetch summary stats
  const fetchSummary = async () => {
    try {
      const response = await api.get('/reports/compliance/summary?days=30');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };
  
  // Apply filters
  const handleApplyFilters = () => {
    fetchData();
  };
  
  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      officer_id: '',
      site_id: '',
      start_date: '',
      end_date: '',
    });
    setTimeout(fetchData, 100);
  };
  
  // Generate report
  const handleGenerateReport = async (shiftId) => {
    setGenerating(shiftId);
    try {
      const response = await api.post(`/reports/compliance/generate/${shiftId}`);
      
      // Show appropriate toast based on email status
      const emailNotif = response.data.email_notification;
      if (emailNotif?.sent && emailNotif?.recipients?.length > 0) {
        toast.success(`Report generated and emailed to ${emailNotif.recipients.length} recipient(s)`);
      } else {
        toast.success('Report generated successfully');
      }
      
      // Update shift in list
      setShifts(shifts.map(s => 
        s.id === shiftId 
          ? { ...s, report_generated: true, report_url: response.data.report?.url }
          : s
      ));
      
      // Auto-download
      if (response.data.report?.url) {
        window.open(`${api.defaults.baseURL}${response.data.report.url}`, '_blank');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(null);
    }
  };
  
  // Download report
  const handleDownloadReport = (shift) => {
    if (shift.report_url) {
      window.open(`${api.defaults.baseURL}${shift.report_url}`, '_blank');
    } else {
      handleGenerateReport(shift.id);
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  return (
    <div className="min-h-screen bg-[#09090b] text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-rajdhani font-bold text-white flex items-center gap-2">
              <FileText className="w-7 h-7 text-emerald-400" />
              Compliance Reports
            </h1>
            <p className="text-zinc-400 mt-1">
              Generate and download shift compliance reports
            </p>
          </div>
          <Button
            variant="outline"
            className="border-[#3f3f46]"
            onClick={() => { fetchData(); fetchSummary(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-[#18181b] border-[#3f3f46]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-white">{summary.completed_shifts}</p>
                <p className="text-xs text-zinc-500">Completed Shifts</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-[#3f3f46]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">{summary.reports_generated}</p>
                <p className="text-xs text-zinc-500">Reports Generated</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-[#3f3f46]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{summary.report_coverage}%</p>
                <p className="text-xs text-zinc-500">Report Coverage</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-[#3f3f46]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{summary.shifts_with_incidents}</p>
                <p className="text-xs text-zinc-500">With Incidents</p>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-[#3f3f46]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{summary.overridden_shifts}</p>
                <p className="text-xs text-zinc-500">Overridden</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Filters */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-rajdhani text-zinc-400 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              FILTERS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select
                value={filters.officer_id || "all"}
                onValueChange={(v) => setFilters({ ...filters, officer_id: v === "all" ? "" : v })}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue placeholder="All Officers" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All Officers</SelectItem>
                  {officers.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.first_name} {o.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={filters.site_id || "all"}
                onValueChange={(v) => setFilters({ ...filters, site_id: v === "all" ? "" : v })}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="bg-[#09090b] border-[#3f3f46]"
                placeholder="Start Date"
              />
              
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="bg-[#09090b] border-[#3f3f46]"
                placeholder="End Date"
              />
              
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={handleApplyFilters}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Apply
                </Button>
                <Button
                  variant="outline"
                  className="border-[#3f3f46]"
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Results */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-rajdhani text-zinc-400 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                COMPLETED SHIFTS
              </span>
              <Badge className="bg-zinc-700 text-zinc-300">{total} shifts</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : shifts.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No completed shifts found</p>
                <p className="text-sm text-zinc-600">Adjust your filters to see more results</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shifts.map((shift) => (
                  <div
                    key={shift.id}
                    className="p-4 bg-[#09090b] rounded-lg border border-[#3f3f46] hover:border-zinc-600 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-white">{shift.site_name}</span>
                          {shift.site_code && (
                            <Badge variant="outline" className="text-xs border-zinc-600">
                              {shift.site_code}
                            </Badge>
                          )}
                          {shift.has_incidents && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Incidents
                            </Badge>
                          )}
                          {shift.is_override && (
                            <Badge className="bg-red-500/20 text-red-400 text-xs">
                              Override
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {shift.officer_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(shift.actual_end)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {shift.duration_hours?.toFixed(1)}h
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {shift.report_generated ? (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleDownloadReport(shift)}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#3f3f46]"
                            onClick={() => handleGenerateReport(shift.id)}
                            disabled={generating === shift.id}
                          >
                            {generating === shift.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 mr-2" />
                            )}
                            Generate Report
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
